import React from "react"
import type { SubmitHandler } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router"

import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import type { Meter } from "@/models/meter"
import type { Site } from "@/models/site"
import {
  type EditMeterSchema,
  MeterImmutableReadonlyFields,
  editMeterSchema,
} from "@/modules/admin/meters"
import { adminEntityFieldsMaxWidthSx } from "@/modules/admin/utils/admin-entity-fields-max-width-sx"
import { FormSelectField } from "@/nzds/forms/form-select-field/form-select-field"
import { FormTextField } from "@/nzds/forms/form-text-field/form-text-field"
import { Form } from "@/nzds/forms/form/form"
import { ResetButton } from "@/nzds/forms/reset-button/reset-button"
import { SubmitButton } from "@/nzds/forms/submit-button/submit-button"
import { useForm } from "@/nzds/forms/use-form/use-form"
import { useUpdateMeter } from "@/services/meter"
import { useUrl } from "@/utils/url"

import { Link as MuiLink, Stack } from "@mui/material"

interface AdminMeterEditFormProps {
  meter: Meter
  meterId: number
  meterIsFetching: boolean
  sites: Site[]
  sitesIsFetching: boolean
}

const meterToEditFormValues = (meter: Meter): EditMeterSchema => ({
  accountNumber: meter.accountNumber ?? "",
  code: meter.meterCode ?? "",
  provider: meter.meterProvider ?? "",
  siteId: meter.site?.id ?? "",
})

export const AdminMeterEditForm: React.FC<AdminMeterEditFormProps> = ({
  meter,
  meterId,
  meterIsFetching,
  sites,
  sitesIsFetching,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation("common")
  const { adminMeterPath, adminMetersPath } = useUrl()

  const form = useForm({
    defaultValues: meterToEditFormValues(meter),
    mode: "onChange",
    schema: editMeterSchema,
  })

  const navigateToShow = () => {
    void navigate(`${adminMeterPath(String(meterId))}${location.search}`, {
      replace: true,
    })
  }

  const { updateMeterMutateAsync, updateMeterIsPending } = useUpdateMeter({
    onSuccess: () => {
      navigateToShow()
    },
  })

  const handleSubmit: SubmitHandler<EditMeterSchema> = async (values) => {
    await updateMeterMutateAsync({
      meter,
      updates: {
        accountNumber: values.accountNumber,
        meterCode: values.code,
        meterProvider: values.provider,
        siteId: values.siteId,
      },
    })
  }

  const currentSiteId = meter.site?.id ?? ""
  const siteOptions = sites.map((site) => ({
    label: site.name,
    value: String(site.id),
  }))

  if (
    currentSiteId &&
    !siteOptions.some((option) => option.value === currentSiteId)
  ) {
    siteOptions.push({
      label: meter.site?.name ?? currentSiteId,
      value: currentSiteId,
    })
  }

  const areFieldsDisabled = meterIsFetching || updateMeterIsPending

  return (
    <Form<EditMeterSchema>
      aria-label={t("editMeter.title")}
      form={form}
      onSubmit={handleSubmit}
    >
      <Stack sx={{ gap: 2 }}>
        <PageTitle
          breadcrumbActions={
            <>
              <MuiLink
                component={Link}
                to={`${adminMetersPath}${location.search}`}
                underline="hover"
              >
                {t("admin.meters")}
              </MuiLink>
              <MuiLink
                component={Link}
                to={`${adminMeterPath(String(meterId))}${location.search}`}
                underline="hover"
              >
                {t("admin.meterDetail.viewMeter")}
              </MuiLink>
            </>
          }
          heading={t("editMeter.title")}
          primaryActions={
            <>
              <ResetButton
                disabled={updateMeterIsPending}
                onClick={navigateToShow}
                variant="outlined"
              >
                {t("button.cancel")}
              </ResetButton>
              <SubmitButton disabled={areFieldsDisabled}>
                {t("button.save")}
              </SubmitButton>
            </>
          }
        />
        <PageCard>
          <Stack sx={{ ...adminEntityFieldsMaxWidthSx, gap: 2 }}>
            <FormTextField<EditMeterSchema>
              disabled={areFieldsDisabled}
              label={t("fields.meterCode.title")}
              name="code"
              required
              variant="stacked"
            />
            <FormSelectField<EditMeterSchema>
              disabled={areFieldsDisabled || sitesIsFetching}
              label={t("fields.siteName.title")}
              name="siteId"
              options={siteOptions}
              required
              variant="stacked"
            />
            <FormTextField<EditMeterSchema>
              disabled={areFieldsDisabled}
              label={t("fields.utilityProvider.title")}
              name="provider"
              required
              variant="stacked"
            />
            <FormTextField<EditMeterSchema>
              disabled={areFieldsDisabled}
              label={t("editMeter.accountNumberLabel")}
              name="accountNumber"
              variant="stacked"
            />
            <MeterImmutableReadonlyFields meter={meter} />
          </Stack>
        </PageCard>
      </Stack>
    </Form>
  )
}
